/**
 * Add To Cart — Mobile
 * ---------------------------------------------------------------------------
 * Drives the mobile-only sticky add-to-cart bar. Two flows:
 *   1. Multi-size products — the trigger opens the #sizes-container popup and
 *      each size button adds its own variant.
 *   2. Unique-size products (single "U" size) or products with no size popup
 *      at all (e.g. Nina Janvier bracelets/necklaces) — the trigger adds the
 *      currently selected variant directly, read from the product form.
 *
 * #sizes-container is OPTIONAL: products without a size selector must not
 * crash the element on construction. Lives inside the section that re-renders
 * on variant/product swap, so connectedCallback re-derives state from the
 * fresh DOM each time and disconnectedCallback tears every listener down.
 */
if (!customElements.get('add-to-cart-mobile')) {
  class AddToCartMobile extends HTMLElement {
    static SELECTORS = {
      trigger: '#addToCartTrigger',
      sizesContainer: '#sizes-container',
      sizeOption: '[data-submit-mobile]',
      closeButton: '.close-btn',
      overlay: '.popup-overlay',
      form: '.shopify-product-form',
      idInput: 'input[name="id"]',
      adjustedSizeInput: 'input[name="properties[Adjusted size]"]',
      addedPopover: 'x-popover.product-added',
      addedBorder: '.product-added .animated-border',
    };
    static ATTRIBUTES = { uniqueSize: 'true', busy: 'aria-busy' };
    static CLASSES = { hidden: 'hidden', animationStarted: 'animation-started' };
    static PROPERTIES = { adjustedSize: 'properties[Adjusted size]' };
    static EVENTS = {
      prepareBundledSections: 'cart:prepare-bundled-sections',
      loadingStart: 'theme:loading:start',
      loadingEnd: 'theme:loading:end',
      variantAdd: 'variant:add',
      cartChange: 'cart:change',
      cartError: 'cart:error',
      cartRefresh: 'cart:refresh',
      toastShow: 'toast:show',
    };
    static CART_TYPES = { page: 'page', message: 'message' };
    static PAGE_TYPES = { cart: 'cart' };
    static ROUTES = { add: 'cart/add.js', cart: 'cart.js', cartPage: 'cart' };
    static MOBILE_MAX_WIDTH = 999;
    static FALLBACK_ERROR = 'Something went wrong';

    connectedCallback () {
      this._abort = new AbortController();
      this._uniqueSize = this.dataset.uniqueSize === AddToCartMobile.ATTRIBUTES.uniqueSize;
      this._trigger = this.querySelector(AddToCartMobile.SELECTORS.trigger);
      this._sizesContainer = document.querySelector(AddToCartMobile.SELECTORS.sizesContainer);
      this._sizeOptions = this._sizesContainer
        ? [...this._sizesContainer.querySelectorAll(AddToCartMobile.SELECTORS.sizeOption)]
        : [];

      this._trigger?.addEventListener('click', () => this._onTriggerClick(), { signal: this._abort.signal });

      this._sizeOptions.forEach((option) =>
        option.addEventListener('click', (event) => {
          event.preventDefault();
          this._addToCart(option.dataset.id, option.dataset.propertyValue || '');
        }, { signal: this._abort.signal })
      );

      if (this._sizesContainer) {
        this._sizesContainer.querySelector(AddToCartMobile.SELECTORS.overlay)
          ?.addEventListener('click', () => this._close(), { signal: this._abort.signal });
        this._sizesContainer.querySelectorAll(AddToCartMobile.SELECTORS.closeButton)
          .forEach((button) => button.addEventListener('click', () => this._close(), { signal: this._abort.signal }));
      }
    }

    disconnectedCallback () {
      this._abort?.abort();
    }

    _onTriggerClick () {
      // Multiple sizes → let the customer pick one from the popup.
      if (!this._uniqueSize && this._sizesContainer) {
        this._open();
        return;
      }
      // Unique size → a single size button carries the effective variant (+ any
      // adjusted-size property for adjustable rings), so add straight from it.
      const sizeOption = this._sizeOptions[0];
      if (sizeOption) {
        this._addToCart(sizeOption.dataset.id, sizeOption.dataset.propertyValue || '');
        return;
      }
      // No size popup at all (bracelets, necklaces…) → add the selected variant.
      this._addToCart(this._selectedVariantId(), '');
    }

    _selectedVariantId () {
      const form = document.querySelector(AddToCartMobile.SELECTORS.form);
      return form?.querySelector(AddToCartMobile.SELECTORS.idInput)?.value || '';
    }

    _open () {
      this._sizesContainer?.classList.remove(AddToCartMobile.CLASSES.hidden);
    }

    _close () {
      this._sizesContainer?.classList.add(AddToCartMobile.CLASSES.hidden);
      document.querySelector(AddToCartMobile.SELECTORS.addedPopover)?.hide();
      document.querySelector(AddToCartMobile.SELECTORS.addedBorder)
        ?.classList.remove(AddToCartMobile.CLASSES.animationStarted);
    }

    _isMobile () {
      return window.innerWidth <= AddToCartMobile.MOBILE_MAX_WIDTH;
    }

    _busyButtons (form) {
      return [...form.elements].filter((element) => element.type === 'submit');
    }

    _bundledSections () {
      const sections = new Set();
      document.documentElement.dispatchEvent(new CustomEvent(AddToCartMobile.EVENTS.prepareBundledSections, {
        bubbles: true,
        detail: { sections },
      }));
      return [...sections];
    }

    _syncAdjustedSizeInput (form, adjustedSizeValue) {
      let input = form.querySelector(AddToCartMobile.SELECTORS.adjustedSizeInput);
      if (adjustedSizeValue) {
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = AddToCartMobile.PROPERTIES.adjustedSize;
          form.appendChild(input);
        }
        input.value = adjustedSizeValue;
      } else if (input) {
        input.remove();
      }
    }

    _buildFormData (form, variantId, adjustedSizeValue) {
      const idInput = form.querySelector(AddToCartMobile.SELECTORS.idInput);
      if (idInput) idInput.value = variantId;

      // "Adjusted size" only applies to adjustable rings bought in an unavailable
      // size; otherwise make sure no stale property leaks into the cart.
      this._syncAdjustedSizeInput(form, adjustedSizeValue);

      const formData = new FormData(form);
      formData.set('id', variantId);
      if (adjustedSizeValue) {
        formData.set(AddToCartMobile.PROPERTIES.adjustedSize, adjustedSizeValue);
      } else {
        formData.delete(AddToCartMobile.PROPERTIES.adjustedSize);
      }
      formData.set('quantity', '1');
      formData.set('sections', this._bundledSections().join(','));
      return formData;
    }

    async _addToCart (variantId, adjustedSizeValue) {
      if (!this._isMobile() || !variantId) return;

      const form = document.querySelector(AddToCartMobile.SELECTORS.form);
      if (!form) return;

      const submitButtons = this._busyButtons(form);
      if (submitButtons.some((button) => button.getAttribute(AddToCartMobile.ATTRIBUTES.busy) === 'true')) return;
      submitButtons.forEach((button) => button.setAttribute(AddToCartMobile.ATTRIBUTES.busy, 'true'));

      const formData = this._buildFormData(form, variantId, adjustedSizeValue);
      document.documentElement.dispatchEvent(new CustomEvent(AddToCartMobile.EVENTS.loadingStart, { bubbles: true }));

      try {
        const response = await fetch(`${Shopify.routes.root}${AddToCartMobile.ROUTES.add}`, {
          method: 'POST',
          body: formData,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        const responseJson = await response.json();

        this._resetButtons(submitButtons);

        if (response.ok) {
          await this._handleSuccess(form, responseJson);
        } else {
          this._close();
          this._handleError(form, responseJson.message);
        }
      } catch (error) {
        this._resetButtons(submitButtons);
        this._handleError(form, error.message || AddToCartMobile.FALLBACK_ERROR);
      }
    }

    _resetButtons (submitButtons) {
      submitButtons.forEach((button) => button.removeAttribute(AddToCartMobile.ATTRIBUTES.busy));
      document.documentElement.dispatchEvent(new CustomEvent(AddToCartMobile.EVENTS.loadingEnd, { bubbles: true }));
    }

    async _handleSuccess (form, responseJson) {
      const settings = window.themeVariables.settings;

      if (settings.cartType === AddToCartMobile.CART_TYPES.page || settings.pageType === AddToCartMobile.PAGE_TYPES.cart) {
        window.location.href = `${Shopify.routes.root}${AddToCartMobile.ROUTES.cartPage}`;
        return;
      }

      const cart = await (await fetch(`${Shopify.routes.root}${AddToCartMobile.ROUTES.cart}`)).json();
      cart.sections = responseJson.sections;
      const items = responseJson.hasOwnProperty('items') ? responseJson.items : [responseJson];

      form.dispatchEvent(new CustomEvent(AddToCartMobile.EVENTS.variantAdd, {
        bubbles: true,
        detail: { items, cart },
      }));
      document.documentElement.dispatchEvent(new CustomEvent(AddToCartMobile.EVENTS.cartChange, {
        bubbles: true,
        detail: { baseEvent: AddToCartMobile.EVENTS.variantAdd, cart },
      }));

      if (settings.cartType === AddToCartMobile.CART_TYPES.message) {
        document.dispatchEvent(new CustomEvent(AddToCartMobile.EVENTS.toastShow, {
          detail: {
            message: window.themeVariables.strings.addedToCart.replace('{{product_title}}', items?.[0]?.title || ''),
          },
        }));
      }

      this._close();
    }

    _handleError (form, message) {
      form.dispatchEvent(new CustomEvent(AddToCartMobile.EVENTS.cartError, {
        bubbles: true,
        detail: { error: message },
      }));
      document.dispatchEvent(new CustomEvent(AddToCartMobile.EVENTS.toastShow, {
        detail: { message, tone: 'error' },
      }));
      document.documentElement.dispatchEvent(new CustomEvent(AddToCartMobile.EVENTS.cartRefresh, { bubbles: true }));
    }
  }

  customElements.define('add-to-cart-mobile', AddToCartMobile);
}
